import Vue from "vue";
import VueRouter from "vue-router";
import {$t} from "../i18n";
import {user} from "../api";
import EmptyRouterView from "../views/EmptyRouterView";
import Main from "../Main.vue";
import RegularWrapper from "../views/RegularWrapper";
import {state as accountState} from "../store/modules/account";
import {convertParamsObjectToQueryString, convertQueryStringToParamsObject} from "@/components/NewFilterBar/FilterBar";
import {state as appState} from "@/store/modules/app";
import {actions} from "@/store/modules/dialog";
import IntercomModule from "@/modules/intercom/intercom";

Vue.use(VueRouter);

const routes = [
    {
        path: "/login",
        name: "login",
        component: () => import(/* webpackChunkName: "SignIn" */ "../views/Entry/SignIn"),
        meta: {
            anonymousAccess: true,
        },
    },
    {
        path: "/signup",
        name: "signup",
        component: () => import(/* webpackChunkName: "SignUp" */ "../views/Entry/SignUp"),
        meta: {
            anonymousAccess: true,
        },
    },
    {
        path: "/signup/success",
        name: "signup_success",
        component: () => import(/* webpackChunkName: "SignUpSuccess" */ "../views/Entry/SignUpSuccess"),
        meta: {anonymousAccess: true}
    },
    {
        path: "/forgot",
        name: "forgot_password",
        component: () => import(/* webpackChunkName: "ForgotPassword" */ "../views/Entry/ForgotPassword"),
        meta: {anonymousAccess: true}
    },
    {
        path: "/forgot/success",
        name: "forgot_password_success",
        component: () => import(/* webpackChunkName: "ForgotPasswordSuccess" */ "../views/Entry/ForgotPasswordSuccess"),
        meta: {anonymousAccess: true}
    },
    {
        path: "/reset",
        name: "reset_password",
        component: () => import(/* webpackChunkName: "ResetPassword" */ "../views/Entry/ResetPassword"),
        meta: {anonymousAccess: true}
    },
    {
        path: "/register",
        name: "accept_invitation",
        beforeEnter: async (to, from, next) => {
            return next({name: "setup_activate", query: to.query});
        },
        meta: {anonymousAccess: true}
    },
    {
        path: "/setup/activate",
        name: "setup_activate",
        component: () => import(/* webpackChunkName: "InvitationSignUp" */ "../views/Entry/InvitationSignUp"),
        meta: {anonymousAccess: true}
    },
    {
        path: "/setup/join",
        name: "setup_join",
        component: () => import(/* webpackChunkName: "InvitationAccept" */ "../views/Entry/InvitationAccept"),
        meta: {anonymousAccess: true}
    },
    {
        path: "/setup/",
        component: () => import(/* webpackChunkName: "SetupCContainer" */ "../views/SetupWizard/SetupContainer"),
        children: [
            {
                path: "new",
                name: "setup_country",
                component: () => import(/* webpackChunkName: "SetupCountry" */ "../views/SetupWizard/SetupCountry"),
                meta: {anonymousAccess: true}
            },
            {
                path: "new/personal",
                name: "setup_personal",
                component: () => import(/* webpackChunkName: "SetupPersonal" */ "../views/SetupWizard/SetupPersonal"),
                meta: {anonymousAccess: true}
            },
            {
                path: "new/company",
                name: "setup_company",
                component: () => import(/* webpackChunkName: "SetupCompany" */ "../views/SetupWizard/SetupCompany"),
                meta: {anonymousAccess: true}
            },
            {
                path: "new/book_demo",
                name: "setup_book_demo",
                component: () =>
                    import(/* webpackChunkName: "SetupBookDemo" */ "../views/SetupWizard/SetupBookDemo"),
                meta: {anonymousAccess: true}
            },
            {
                path: "new/finishing",
                name: "setup_finish",
                component: () =>
                    import(/* webpackChunkName: "SetupFinishing" */ "../views/SetupWizard/SetupSpinner"),
                meta: {anonymousAccess: true}
            }
        ]
    },
    {
        path: "/",
        component: Main,
        children: [
            //irregular size/full width pages are not contained by regular wrapper
            {
                path: "/abiline",
                name: "abiline",
                meta: {
                    title: "abiline"
                },
                component: () => import("../views/Admin/./Admin"),
                redirect: {name: "abiline_u"},

                children: [
                    {
                        path: "users",
                        name: "abiline_u",
                        component: () => import("../views/Admin/AdminMember.vue"),
                        meta: {
                            title: "Users",
                            navActiveParent: "abiline"
                        }
                    },
                    {
                        path: "companies",
                        name: "abiline_c",
                        component: () => import("../views/Admin/./AdminCompany"),
                        meta: {
                            title: "Companies",
                            navActiveParent: "abiline"
                        }
                    },
                    {
                        path: "leads",
                        name: "abiline_l",
                        component: () => import("../views/Admin/./AdminLead"),
                        meta: {
                            title: "Leads",
                            navActiveParent: "abiline"
                        }
                    },
                    {
                        path: "demos",
                        name: "abiline_d",
                        component: () => import("../views/Admin/./AdminDemo"),
                        meta: {
                            title: "Demos",
                            navActiveParent: "abiline"
                        }
                    },

                    {
                        path: "calendar",
                        name: "abiline_k",
                        component: () => import("../views/Admin/./AdminCalendar"),
                        meta: {
                            title: "Calendar",
                            navActiveParent: "abiline"
                        }
                    },
                    {
                        path: "help",
                        name: "abiline_h",
                        component: () => import("../views/Admin/./AdminHelp"),
                        meta: {
                            title: "Help",
                            navActiveParent: "abiline"
                        }
                    },
                    {
                        path: "security/:object/:id",
                        name: "abiline_o",
                        component: () => import("../views/Admin/ObjectSecurityView"),
                        meta: {
                            title: "ObjSec",
                            hidesBehind: "abiline"
                        }
                    },
                    {
                        path: "vouchers",
                        name: "abiline_v",
                        component: () => import("../views/Admin/./AdminVoucher"),
                        meta: {
                            title: "Vouchers",
                            navActiveParent: "abiline"
                        }
                    },
                    {
                        path: "kpi",
                        name: "abiline_kpi",
                        component: () => import("../views/Admin/./AdminKpi.vue"),
                        meta: {
                            title: "KPI",
                            navActiveParent: "abiline"
                        }
                    },
                ]
            },
            {
                path: "/calendar",
                name: "calendar",
                meta: {
                    title: $t("Calendar"),
                    color: "secondary",
                    footerSpacing: false
                },
                component: () => import(/* webpackChunkName: "Calendar" */ "../views/Calendar")
            },
            // regular center of page components are children of regular wrapper
            {
                path: "/",
                component: RegularWrapper,
                children: [
                    {
                        path: "/",
                        name: "dashboard",
                        component: () => import(/* webpackChunkName: "Dashboard" */ "../views/Dashboard/Dashboard.vue"),
                        meta: {
                            color: "app-gradient-alpha",
                            title: $t("Dashboard"),
                            fluid: true,
                            noPadding: true
                        }
                    },
                    {
                        path: "/activity/",
                        component: () => import(/* webpackChunkName: "ProfileLogs" */ "../views/ProfileLogs"),
                        children: [
                            {
                                path: "notifications",
                                name: "notifications",
                                meta: {
                                    title: $t("Notifications"),
                                    color: "app-gradient-alpha"
                                },
                                component: () =>
                                    import(
                                        /* webpackChunkName: "NotificationsList" */ "../views/ProfileLogs/NotificationsList"
                                        )
                            },
                            {
                                path: "activitylog",
                                name: "activitylog",
                                meta: {
                                    title: $t("Activity log"),
                                    color: "app-gradient-alpha"
                                },
                                component: () =>
                                    import(/* webpackChunkName: "ActivityLog" */ "../views/ProfileLogs/ActivityList")
                            }
                        ]
                    },
                    {
                        path: "/report",
                        name: "report",
                        meta: {
                            title: $t("Report"),
                            color: "purple",
                            fluid: true,
                            noBottomPadding: true
                        },
                        component: () => import(/* webpackChunkName: "ActivityLog" */ "../views/Report/ReportWrapper")
                    },
                    {
                        path: "/monitoring",
                        name: "monitoring",
                        meta: {
                            title: $t("Monitoring"),
                            color: "purple darken-1",
                            fluid: true,
                        },
                        component: () => import(/* webpackChunkName: "ActivityLog" */ "../views/Monitoring/MonitoringView"),
                    },
                    {
                        path: "/invoices",
                        name: "billing",
                        component: () => import(/* webpackChunkName: "SalesInvoiceList" */ "../views/Invoice/SalesInvoiceList"),
                        meta: {
                            title: $t("Billing"),
                            color: "tretiary",
                            fluid: true,
                        }
                    },
                    {
                        path: "/invoices/:id",
                        name: "invoice_edit",
                        component: () =>
                            import(/* webpackChunkName: "SalesInvoiceEdit" */ "../views/Invoice/SalesInvoiceEdit"),
                        meta: {
                            title: $t("Invoice"),
                            fluid: false,
                            sidebar: {relatedType: "client", subType: "invoice"}
                        }
                    },
                    {
                        path: "/new-invoice/:clientId/:accountId",
                        name: "new_invoice",
                        component: () =>
                            import(/* webpackChunkName: "NewInvoice" */ "../views/Invoice/SalesInvoiceEdit"),
                        meta: {
                            title: $t("Invoice"),
                            fluid: true,
                        }
                    },
                    {
                        path: "/clients",
                        name: "clients_list",
                        component: () =>
                            import(/* webpackChunkName: "ClientsList" */ "../views/Client/ClientsList/ClientList"),
                        meta: {
                            title: $t("Clients"),
                            fluid: true,
                            noBottomPadding: true
                        }
                    },
                    {
                        path: "/clients/monitoring",
                        name: "clients_list_monitoring",
                        component: () =>
                            import(/* webpackChunkName: "ClientsListMonitoring" */ "../views/Client/MonitoringList/ClientsListMonitoring.vue"),
                        meta: {
                            title: $t("Clients - Monitoring"),
                            fluid: true,
                            noBottomPadding: true
                        }
                    },
                    {
                        path: "/projects",
                        name: "projects_list",
                        component: () =>
                            import(/* webpackChunkName: "ProjectsList" */ "../views/Project/ProjectsList.vue"),
                        meta: {
                            dynamicTitle: true,
                            fluid: true,
                            noBottomPadding: true,
                            newTaskSetProjectId: true
                        }
                    },
                    {
                        path: "/projects/:id/",
                        name: "project_view",
                        component: () =>
                            import(/* webpackChunkName: "ProjectsList" */ "../views/Project/ProjectView.vue"),
                        redirect: {name: "project_tasks"},
                        meta: {
                            navActiveParent: "projects_list",
                            dynamicTitle: true,
                            fluid: true,
                            newTaskSetProjectId: true
                        },
                        children: [
                            {
                                path: "tasks",
                                name: "project_tasks",
                                component: () =>
                                    import(
                                        /* webpackChunkName: "ProjectsList" */ "../views/Project/ProjectTasks"
                                        ),
                                meta: {
                                    parent_path: "projects_list",
                                    navActiveParent: "projects_list",
                                    dynamicTitle: true,
                                    sidebar: {relatedType: "project"},
                                    newTaskSetProjectId: true
                                }
                            },
                            {
                                path: "notes",
                                name: "project_notes",
                                component: () =>
                                    import(
                                        /* webpackChunkName: "ProjectsList" */ "../views/Project/ProjectNotesView.vue"
                                        ),
                                meta: {
                                    parent_path: "projects_list",
                                    navActiveParent: "projects_list",
                                    dynamicTitle: true,
                                    sidebar: {relatedType: "project"},
                                    newTaskSetProjectId: true
                                }
                            },
                            {
                                path: "communication",
                                name: "project_communication",
                                component: () =>
                                    import(
                                        /* webpackChunkName: "ProjectsList" */ "../views/Project/ProjectCommunication.vue"
                                        ),
                                meta: {
                                    parent_path: "projects_list",
                                    navActiveParent: "projects_list",
                                    dynamicTitle: true,
                                    sidebar: {relatedType: "project"},
                                    newTaskSetProjectId: true
                                }
                            },
                            {
                                path: "activity",
                                name: "project_activity",
                                component: () =>
                                    import(
                                        /* webpackChunkName: "ProjectsList" */ "../views/Project/ProjectActivityView.vue"
                                        ),
                                meta: {
                                    parent_path: "projects_list",
                                    navActiveParent: "projects_list",
                                    dynamicTitle: true,
                                    sidebar: {relatedType: "project"},
                                    newTaskSetProjectId: true
                                }
                            }
                        ]
                    },
                    {
                        path: "/projects/:account_id/new_project/:project_type_id",
                        name: "project_new",
                        component: () => import(/* webpackChunkName: "ProjectsList" */ "../views/Project/ProjectEditView.vue"),
                        meta: {
                            navActiveParent: "projects_list",
                            dynamicTitle: true
                        }
                    },
                    {
                        path: "/projects/:id/edit",
                        name: "project_edit",
                        component: () => import(/* webpackChunkName: "ProjectsList" */ "../views/Project/ProjectEditView.vue"),
                        meta: {
                            navActiveParent: "projects_list",
                            dynamicTitle: true,
                            newTaskSetProjectId: true
                        }
                    },
                    {  // Project workflow
                        path: "/projects/:id/edit_workflow/:action",
                        name: "project_edit_workflow",
                        component: () => import("../views/Client/ClientPlanView.vue"),
                        meta: {
                            title: $t("Project"),
                            navActiveParent: "projects_list",
                            type: "project",
                            sidebar: {relatedType: "project"},
                        }
                    },
                    {
                        path: "/suppliers",
                        name: "suppliers_list",
                        component: () =>
                            import(/* webpackChunkName: "SuppliersList" */ "../views/Supplier/SuppliersList.vue"),
                        meta: {
                            title: $t("Suppliers"),
                            noBottomPadding: true,
                            fluid: true
                        }
                    },
                    {
                        path: "/suppliers/:account_id/new_supplier/:type_id",
                        name: "supplier_new",
                        component: () => import(/* webpackChunkName: "SuppliersList" */ "../views/Supplier/SupplierEditView.vue"),
                        meta: {
                            title: $t("Add supplier"),
                            navActiveParent: "suppliers_list"
                        }
                    },
                    {
                        path: "/suppliers/:id/edit",
                        name: "supplier_edit",
                        component: () => import(/* webpackChunkName: "SuppliersList" */ "../views/Supplier/SupplierEditView.vue"),
                        meta: {
                            title: $t("Edit supplier"),
                            navActiveParent: "suppliers_list"
                        }
                    },
                    {
                        path: "/suppliers/:id",
                        name: "supplier",
                        component: () => import(/* webpackChunkName: "SuppliersList" */ "../views/Supplier/SupplierView.vue"),
                        meta: {
                            title: $t("Supplier"),
                            navActiveParent: "suppliers_list"
                        }
                    },
                    {
                        path: "/links",
                        name: "links",
                        component: () =>
                            import(/* webpackChunkName: "BookmarksList" */ "../views/Bookmark/BookmarksList.vue"),
                        meta: {
                            title: $t("Links")
                        }
                    },
                    {
                        path: "/store/:accountId?",
                        name: "app_store",
                        component: () => import(/* webpackChunkName: "AppStore" */ "../views/AppStore/AppStore.vue"),
                        meta: {
                            title: $t("Settings & Apps")
                        }
                    },
                    {  // Job template - new
                        path: "/accounts/:account_id/new_job",
                        name: "job_new",
                        component: () => import("../views/Client/ClientPlanView.vue"),
                        meta: {
                            title: $t("New job"),
                            type: "job"
                        },
                        props: function (route) {
                            return {
                                is_client_portal: route.query.is_client_portal
                            };
                        },
                    },
                    {  // Workflow template - new
                        path: "/accounts/:account_id/new_workflow",
                        name: "workflow_new",
                        component: () => import("../views/Client/ClientPlanView.vue"),
                        meta: {
                            title: $t("New workflow"),
                            type: "workflow"
                        }
                    },
                    {  // Job template - edit/clone
                        path: "/accounts/:accountId/contract_template/:contractId",
                        name: "new_contract_template",
                        component: () => import("../views/Client/Billing/Contract/EditTemplate/ClientBillingContractEditTemplate.vue"),
                        meta: {
                            title: $t("New template")
                        }
                    },
                    {
                        path: "/accounts/:account_id/jobs/:id/edit/:use_template",
                        name: "job_edit",
                        component: () => import("../views/Client/ClientPlanView.vue"),
                        meta: {
                            title: $t("Edit job"),
                            type: "job"
                        }
                    },
                    {  // Workflow template - edit/clone
                        path: "/accounts/:account_id/workflows/:id/edit/:use_template",
                        name: "workflow_edit",
                        component: () => import("../views/Client/ClientPlanView.vue"),
                        meta: {
                            title: $t("Edit workflow"),
                            type: "workflow"
                        }
                    },
                    {
                        path: "/accounts/:account_id/new_import",
                        name: "import_new",
                        component: () => import("../views/Import/Xlsx/FileUpload/ImportClient.vue"),
                        meta: {
                            title: $t("Import Clients from Excel")
                        }
                    },
                    {
                        path: "/accounts/:account_id/import_selection",
                        name: "import_selection",
                        component: () => import("../views/Import/ImportSelectionView"),
                        meta: {
                            title: $t("Import clients"),
                            navActiveParent: "clients_list"
                        }
                    },

                    {
                        path: "/accounts/:account_id/import_client_workflow",
                        name: "import_client_workflow",
                        meta: {title: "import_client_workflow"},
                        component: () => import("../views/Import/Xlsx/FileUpload/ImportWorkflow.vue"),
                        props: function (route) {
                            return {
                                relatedType: 'client_workflow',
                            };
                        }
                    },
                    {
                        path: "/accounts/:account_id/import_project_workflow",
                        name: "import_project_workflow",
                        meta: {title: "import_project_workflow"},
                        component: () => import("../views/Import/Xlsx/FileUpload/ImportWorkflow.vue"),
                        props: function (route) {
                            return {
                                relatedType: 'project_workflow',
                            };
                        }
                    },

                    {
                        path: "/tasks/:id",
                        name: "task",
                        component: () => import("../views/Task/TaskMobileView.vue"),
                        props: function (route) {
                            return {
                                taskId: route.params.id,
                                title: route.query.title,
                                clientId: route.query.client_id,
                                topicId: route.query.topic_id
                            };
                        },
                        meta: {
                            title: $t("Task"),
                            is_mobile_dialog: true
                        }
                    },
                    {
                        path: "/tasks/:id/new_task_template",
                        name: "task_to_task_template",
                        component: () => import("../views/Task/TaskMobileView.vue"),
                        props: function (route) {
                            return {
                                taskId: route.params.id,
                                newTaskTemplate: true
                            };
                        },
                        meta: {
                            title: $t("New recurring task"),
                            is_mobile_dialog: true
                        }
                    },
                    {
                        path: "/clients/:client_id/new_task",
                        name: "new_client_task",
                        component: () => import("../views/Task/TaskMobileView.vue"),
                        props: function (route) {
                            return {
                                clientId: route.params.client_id
                            };
                        },
                        meta: {
                            title: $t("New task"),
                            is_mobile_dialog: true
                        }
                    },
                    {
                        path: "/clients/:client_id/new_task_template",
                        name: "new_client_task_template",
                        component: () => import("../views/Task/TaskMobileView.vue"),
                        props: function (route) {
                            return {
                                clientId: route.params.client_id,
                                newTaskTemplate: true
                            };
                        },
                        meta: {
                            title: $t("New recurring task"),
                            is_mobile_dialog: true
                        }
                    },
                    {
                        path: "/clients/:client_id/:id/new_task_template",
                        name: "client_task_to_task_template",
                        component: () => import("../views/Task/TaskMobileView.vue"),
                        props: function (route) {
                            return {
                                clientId: route.params.client_id,
                                taskId: route.params.id,
                                newTaskTemplate: true
                            };
                        },
                        meta: {
                            title: $t("New recurring task"),
                            is_mobile_dialog: true
                        }
                    },
                    {
                        path: "/clients/:client_id/tasks/:id",
                        name: "client_task",
                        component: () => import("../views/Task/TaskMobileView.vue"),
                        props: function (route) {
                            return {
                                clientId: route.params.client_id,
                                taskId: route.params.id
                            };
                        },
                        meta: {
                            title: $t("Task"),
                            is_mobile_dialog: true
                        }
                    },
                    {
                        path: "/importer",
                        name: "import_edit",
                        component: () => import("../views/Import/Xlsx/FileUpload/ImportClient.vue"),
                        meta: {
                            title: $t("Importer")
                        }
                    },
                    {
                        path: "/importer/:id/settings",
                        name: "import_settings",
                        component: () => import("../views/Import/Xlsx/Settings/Index"),
                        meta: {
                            title: $t("Import clients from Excel"),
                            fluid: true
                        }
                    },
                    {
                        path: "/importer/:id/import_result",
                        name: "import_result",
                        component: () => import("../views/Import/Result/ImportResultView"),
                        meta: {
                            title: $t("Preview"),
                            navActiveParent: "clients_list",
                            fluid: true,
                            dynamicTitle: true,
                            noBottomPadding: true
                        }
                    },
                    {
                        path: "/importer/:id/status",
                        name: "import_status",
                        component: () => import("../views/Import/Status/Index"),
                        meta: {
                            title: $t("Import clients"),
                            navActiveParent: "clients_list",
                            dynamicTitle: true
                        }
                    },
                    {
                        path: "/accounts/:account_id/new_client",
                        name: "client_new",
                        component: () => import("../views/Client/Edit/ClientEditView.vue"),
                        meta: {
                            title: $t("Add client"),
                            navActiveParent: "clients_list"
                        }
                    },
                    {
                        path: "/clients/:id/edit",
                        name: "client_edit",
                        component: () => import("../views/Client/Edit/ClientEditView.vue"),
                        meta: {
                            dynamicTitle: true,
                        }
                    },
                    {  // Client job
                        path: "/clients/:id/edit_plan",
                        name: "client_edit_plan",
                        component: () => import("../views/Client/ClientPlanView.vue"),
                        meta: {
                            title: $t("Client") + " - " + $t("Plan"),
                            navActiveParent: "clients_list",
                            type: "client",
                            sidebar: {relatedType: "client"},
                        }
                    },
                    {
                        path: "/clients/:id/",
                        name: "client_profile",
                        component: () =>
                            import(/* webpackChunkName: "ClientProfile" */ "../views/Client/ClientProfile.vue"),
                        redirect: {name: "client_profile_tasks"},
                        meta: {
                            title: $t("Client") + " - " + $t("Tasks"),
                            headerNavigation: true
                        },
                        children: [
                            {
                                path: "tasks",
                                name: "client_profile_tasks",
                                component: () =>
                                    import(
                                        /* webpackChunkName: "ClientProfile" */ "../views/Client/ClientTasks.vue"
                                        ),
                                meta: {
                                    title: $t("Client") + " - " + $t("Tasks"),
                                    parent_path: "clients_list",
                                    navActiveParent: "clients_list",
                                    headerNavigation: true,
                                    sidebar: {relatedType: "client"}
                                }
                            },
                            {
                                path: "plan",
                                name: "client_profile_plan",
                                component: () =>
                                    import(
                                        /* webpackChunkName: "ClientProfile" */ "../views/Client/ClientProfilePlan.vue"
                                        ),
                                meta: {
                                    title: $t("Client") + " - " + $t("Plan"),
                                    navActiveParent: "clients_list",
                                    headerNavigation: true,
                                    sidebar: {relatedType: "client"}
                                }
                            },
                            {
                                path: "projects",
                                name: "client_profile_projects",
                                component: () =>
                                    import(
                                        /* webpackChunkName: "ClientProfile" */ "../views/Client/Projects/Index.vue"
                                        ),
                                meta: {
                                    dynamicTitle: true,
                                    navActiveParent: "clients_list",
                                    headerNavigation: true,
                                    noBottomPadding: true,
                                    sidebar: {relatedType: "client"}
                                }
                            },
                            {
                                path: "notes",
                                name: "client_profile_notes", //siin
                                component: () =>
                                    import(
                                        /* webpackChunkName: "ClientProfile" */ "../views/Note/NotesView.vue"
                                        ),
                                meta: {
                                    title: $t("Client") + " - " + $t("Notes"),
                                    parent_path: "clients_list",
                                    navActiveParent: "clients_list",
                                    headerNavigation: true,
                                    sidebar: {relatedType: "client"}
                                },
                                props: function (route) {
                                    return {
                                        relatedType: "client",
                                        relatedId: parseInt(route.params.id)
                                    };
                                },
                            },
                            {
                                path: "billing/:accountId",
                                name: "client_profile_billing",
                                component: () =>
                                    import(
                                        /* webpackChunkName: "ClientProfile" */ "../views/Client/ClientProfileBilling.vue"
                                        ),
                                redirect: {name: "client_invoices"},
                                meta: {
                                    title: $t("Client") + " - " + $t("Billing") + " - " + $t("Invoices"),
                                    navActiveParent: "clients_list",
                                    headerNavigation: true,
                                    sidebar: {relatedType: "client"}
                                },
                                children: [
                                    {
                                        path: "invoices",
                                        name: "client_invoices",
                                        component: () =>
                                            import(
                                                /* webpackChunkName: "ClientProfile" */ "../views/Client/ClientInvoicesTab.vue"
                                                ),
                                        meta: {
                                            title: $t("Client") + " - " + $t("Billing") + " - " + $t("Invoices"),
                                            navActiveParent: "client_profile_billing",
                                            headerNavigation: true,
                                            sidebar: {relatedType: "client"}
                                        }
                                    },
                                    {
                                        path: "contract",
                                        name: "client_contract_view",
                                        component: () =>
                                            import(
                                                /* webpackChunkName: "ClientProfile" */ "../views/Client/Billing/Contract/List/ClientBillingContractList.vue"
                                                ),
                                        meta: {
                                            title: $t("Client") + " - " + $t("Billing") + " - " + $t("Contracts"),
                                            navActiveParent: "client_profile_billing",
                                            headerNavigation: true,
                                            sidebar: {relatedType: "client"}
                                        },
                                    },
                                    {
                                        path: "contract/:contractId",
                                        name: "client_contract_edit",
                                        component: () =>
                                            import(
                                                /* webpackChunkName: "ClientProfile" */ "../views/Client/Billing/Contract/Edit/ClientBillingContractEdit.vue"
                                                ),
                                        meta: {
                                            title: $t("Client") + " - " + $t("Billing") + " - " + $t("Contract"),
                                            navActiveParent: "client_profile_billing",
                                            lgAndDownFluid: true,
                                            sidebar: {relatedType: "client"}
                                        }
                                    },
                                    {
                                        path: "settings",
                                        name: "client_billing_settings",
                                        component: () =>
                                            import(
                                                /* webpackChunkName: "ClientProfile" */ "../views/Client/ClientContractSettingsTab.vue"
                                                ),
                                        meta: {
                                            title: $t("Client") + " - " + $t("Billing") + " - " + $t("Settings"),
                                            navActiveParent: "client_profile_billing",
                                            headerNavigation: true,
                                            sidebar: {relatedType: "client"}
                                        }
                                    },
                                ]
                            },
                            {
                                path: "overview",
                                name: "client_profile_overview",
                                component: () =>
                                    import(
                                        /* webpackChunkName: "ClientProfile" */ "../views/Client/ClientProfileOverview.vue"
                                        ),
                                meta: {
                                    title: $t("Client") + " - " + $t("Overview"),
                                    navActiveParent: "clients_list",
                                    headerNavigation: true,
                                    sidebar: {relatedType: "client", forceToggle: true}
                                }
                            },
                            {
                                path: "communication",
                                name: "client_profile_communication",
                                component: () =>
                                    import(
                                        /* webpackChunkName: "ClientProfile" */ "../views/Client/Communication/Index.vue"
                                        ),
                                meta: {
                                    title: $t("Client") + " - " + $t("Communication"),
                                    navActiveParent: "clients_list",
                                    headerNavigation: true,
                                    sidebar: {relatedType: "client"}
                                }
                            }
                        ]
                    },
                    {
                        path: "/users",
                        name: "users_list",
                        component: () => import(/* webpackChunkName: "UsersList" */ "../views/Users/AccountMembersList.vue"),
                        meta: {
                            title: $t("Members"),
                            fluid: true,
                            noBottomPadding: true
                        }
                    },
                    {
                        path: "create_company",
                        name: "create_company",
                        component: () =>
                            import(/* webpackChunkName: "CompanyCreate" */ "../views/Company/CompanyEditView.vue"),
                        meta: {
                            title: $t("Account settings")
                        }
                    },
                    {
                        path: "/profile/edit",
                        name: "profile_edit",
                        component: () => import(/* webpackChunkName: "ProfileEdit" */ "../views/Profile/ProfileEdit"),
                        meta: {
                            title: $t("Profile"),
                            navActiveParent: "profile",
                        }
                    },
                    {
                        path: "/profile/:personId",
                        name: "profile",
                        component: () => import(/* webpackChunkName: "Profile" */ "../views/Profile/ProfileView"),
                        redirect: {name: "profile_info", params: {personId: 0}},
                        meta: {
                            title: $t("Profile"),
                            fluid: true,
                            noPadding: true,
                            navActiveParent: "profile",
                        },
                        children: [
                            {
                                path: "info/:companyId?",
                                name: "profile_info",
                                component: () => import(/* webpackChunkName: "ProfilePreview" */ "../views/Profile/Preview/ProfilePreview"),
                                meta: {
                                    title: $t("Profile"),
                                    fluid: true,
                                    noPadding: true,
                                    navActiveParent: "profile",
                                }
                            },
                            {
                                path: "vacations/:companyId?",
                                name: "profile_vacations",
                                component: () => import(/* webpackChunkName: "ProfileVacations" */ "../views/Profile/Vacations/ProfileVacations"),
                                meta: {
                                    title: $t("Profile"),
                                    fluid: true,
                                    noPadding: true,
                                    navActiveParent: "profile",
                                }
                            },
                        ]
                    },
                    {
                        path: "company/:id/edit",
                        name: "settings_company_edit",
                        component: () =>
                            import(/* webpackChunkName: "CompanyEdit" */ "../views/Company/CompanyEditView.vue"),
                        meta: {
                            navActiveParent: "settings",
                            title: $t("Account settings")
                        }
                    },
                    {
                        path: "/subscription/:id/xero/return",
                        name: "subscription_xero_return",
                        component: () => import(/* webpackChunkName: "Subscription" */ "../views/Subscription/./XeroReturn"),
                        meta: {wizard_mode: true}
                    },
                    {
                        path: "/subscription/:id/choose_plan",
                        name: "subscription_choose_plan",
                        component: () =>
                            import(/* webpackChunkName: "Subscription" */ "../views/Subscription/./Subscription"),
                        meta: {wizard_mode: true}
                    },
                    {
                        path: "/help",
                        name: "help",
                        component: EmptyRouterView,
                        meta: {
                            title: $t("Help")
                        }
                    }
                ]
            },
            {
                path: "/empty",
                name: "empty",
                meta: {
                    title: $t("Empty")
                }
            }
        ]
    },
    {
        path: "/app/ukuwindow_tasks",
        name: "ukuwindow_tasks",
        component: () => import(/* webpackChunkName: "V2MiniUku" */ "../views/Miniuku/UpgradePluginV2"),
        meta: {anonymousAccess: true}
    },
    {
        path: "/:pathMatch(.*)*",
        name: "not_found",
        redirect: {name: "dashboard"}
    }
];

const router = new VueRouter({
    lastUrl: null,
    scrollBehavior: function (to, from, savedPosition) {
        if (from.name === to.name) {
            return {x: 0, y: window.scrollY};
        }

        return {x: 0, y: 0};
    },
    mode: "history",
    // base: process.env.BASE_URL,
    base: "/",

    // set custom query resolver
    parseQuery(query) {
        return convertQueryStringToParamsObject(query)
    },
    stringifyQuery(params) {
        let result = convertParamsObjectToQueryString(params)
        return result ? result : '';
    },
    routes
});

router.beforeEach(function (to, from, next) {
    // check Intercom messages
    IntercomModule.updateIntercom()

    if ((accountState.current_account === undefined || accountState.current_account.id === 0) && !to.matched.some(x => x.meta.anonymousAccess)) {
        next({
            name: "login",
            params: {nextUrl: to.fullPath}
        });
        return;
    }
    // console.log('BEFORE EACH :: ' + to.name + " :: router.beforeEach ->", to)
    router.lastUrl = from.fullPath;
    if (window.self !== window.top) {
        if (to.query.ukuwinversion === undefined) {
            to.query.ukuwinversion = "1.39";
        }
    } else {
        if (to.query.ukuwinversion !== undefined) {
            delete to.query.ukuwinversion;
        }
    }

    if (appState.isNavigationWarning) {
        return clientTemplateNavWarning(next)
    }

    appState.isNavigationWarning = false
    next();
});

export default router;


const clientTemplateNavWarning = (next) => {
    actions.setDialogOptions({
        component: () => import("@/views/Dialog/NavigationWarningDialog"),
        payload: {
            type: "clients_plan",
            title: $t("Leave page?"),
            description: $t(
                "Changes that you made may not be saved"
            ),
            cancelButton: $t("Cancel"),
            confirmButton: $t("Leave"),
            nextVal: () => {
                appState.isNavigationWarning = false
                next()
            }
        }
    });
    actions.openDialog();
    appState.router_key = true
}